<script setup>
import debounce from "just-debounce-it";

const { value, className, isDisabled, isConsultable } = defineProps({
  value: String | null,
  className: String | null,
  isDisabled: Boolean,
  isConsultable: {
    type: Boolean,
    default() {
      return undefined;
    },
  },
});

const emits = defineEmits(["onChange"]);

const selected = ref();
const selectedOption = ref();
const debounceFilter = ref();

const updateFilter = (keyword) => {
  debounceFilter.value = keyword;
};

const debouncedInput = debounce(updateFilter, 600);

const { data: universities } = useApiFetch("universities", {
  key: "universities",
  lazy: true,
  query: {
    name: debounceFilter,
    ...(isConsultable !== undefined && { is_consultable: isConsultable }),
  },
  watch: [debounceFilter],
  transform(universities) {
    return universities?.data;
  },
});

const onSelect = (value) => {
  emits("onChange", value);
  selectedOption.value = universities.value.find((uni) => uni.id === value);
};

onMounted(async () => {
  if (value) {
    selected.value = value;
    await useApiFetch(`universities/${value}`, {
      key: "university-by-id",
      transform(university) {
        return university?.data;
      },
      onResponse({ response }) {
        selectedOption.value = response._data?.data;
      },
    });
  }
});
</script>

<template>
  <div v-if="selectedOption && isDisabled">
    <ProfileAvatar :name="selectedOption?.name" size="sm" />
  </div>
  <Select
    v-else
    v-model="selected"
    :options="universities"
    filter
    optionLabel="name"
    optionValue="id"
    placeholder="Select a university"
    class="w-full rounded-lg text-sm"
    :class="className"
    @filter="(e) => debouncedInput(e.value)"
    @change="(e) => onSelect(e.value)"
    :disabled="isDisabled"
  >
    <template #value="slotProps">
      <div v-if="selectedOption">
        <div class="flex items-center gap-3">
          <Avatar
            :label="selectedOption?.name?.at(0)"
            size="normal"
            shape="circle"
            class="!w-6 !h-6 min-h-6 min-w-6 text-sm"
            style="background-color: #dee9fc; color: #1a2551"
          />
          <p
            class="text-sm"
            :class="
              selectedOption?.deleted_at
                ? 'text-gray-500 hover:text-gray-600'
                : 'text-blue-500 hover:text-blue-600'
            "
          >
            {{ selectedOption?.name }}
          </p>
        </div>
      </div>
      <span v-else>
        {{ slotProps.placeholder }}
      </span>
    </template>
    <template #option="slotProps">
      <div class="flex items-center gap-2">
        <Avatar
          :label="slotProps?.option?.name?.at(0)"
          size="normal"
          shape="circle"
          class="!w-6 !h-6 min-h-6 min-w-6 text-sm"
          style="background-color: #dee9fc; color: #1a2551"
        />
        <p class="text-sm">{{ slotProps?.option?.name }}</p>
      </div>
    </template>
  </Select>
</template>
